import { signal, computed } from "@preact/signals-core";
import { productData, selectedVariantId } from "./product-store";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomizationConfig {
  customSkuCost: number;
  minCharacters: number;
  maxCharacters: number;
  customSkuVariantId: string | null;
  previewUrl: string | null;
  isMandatory: boolean;
  useCustomSku: boolean;
  /**
   * Raw `ships_by-*` tag value (tag with the `ships_by-` prefix stripped) read
   * from the custom SKU product. Personalized items ship later than the base
   * product, so this date is applied to the customized line item instead of the
   * main product's ships-by date. `null` when the custom SKU has no such tag.
   */
  customSkuShipsBy: string | null;
}

// ─── Configuration ────────────────────────────────────────────────────────────

/** Customization configuration (from Liquid/theme settings) */
export const config = signal<CustomizationConfig>({
  customSkuCost: 10,
  minCharacters: 1,
  maxCharacters: 20,
  customSkuVariantId: null,
  previewUrl: null,
  isMandatory: false,
  useCustomSku: false,
  customSkuShipsBy: null,
});

/**
 * Resolve the ships-by date for a personalized item: the custom SKU product's
 * `ships_by-*` tag when present, otherwise the `defaultShippingDate` theme
 * setting. Returns the raw tag value (e.g. "5days" / "2026-12-01"), or null.
 */
export const getCustomSkuShipsBy = (): string | null => {
  return config.value.customSkuShipsBy || ((window as any).defaultShippingDate as string | undefined) || null;
};

// ─── Runtime State ────────────────────────────────────────────────────────────

/** Custom text value entered by user */
export const customText = signal<string>("");

/** Whether the user wants to customize (for confirmation modal) */
export const userWantsToCustomize = signal<boolean>(false);

/** Preview image URL (blob or regular URL) */
export const previewImageUrl = signal<string | null>(null);

/** Loading state for preview image */
export const isLoadingPreview = signal<boolean>(false);

/** Whether the customization modal is open */
export const isCustomizationModalOpen = signal<boolean>(false);

/** Whether the confirmation modal is open */
export const isConfirmationModalOpen = signal<boolean>(false);

// ─── Computed Values ──────────────────────────────────────────────────────────

/** Whether the custom text is valid (meets minimum character requirement) */
export const isCustomTextValid = computed(() => {
  const text = customText.value.trim();
  return text.length >= config.value.minCharacters;
});

/** Whether the product has customization enabled (has "customizable" tag) */
export const isCustomizable = computed(() => {
  const product = productData.value;
  if (!product || !product.tags) return false;
  return product.tags.includes("customizable");
});

/** Whether to show the "Customize It" button */
export const shouldShowCustomizeButton = computed(() => {
  return isCustomizable.value && !config.value.isMandatory;
});

// ─── Actions ──────────────────────────────────────────────────────────────────

/**
 * Initialize customization system with configuration from Liquid.
 */
export const initCustomization = (customConfig: Partial<CustomizationConfig>) => {
  config.value = { ...config.value, ...customConfig };
};

/**
 * Set custom text value and validate it.
 */
export const setCustomText = (text: string) => {
  // Validate: only alphabetic + spaces, uppercase, max length
  let sanitized = text
    .replace(/[^A-Z\s]/gi, "") // Remove non-alphabetic
    .toUpperCase()
    .substring(0, config.value.maxCharacters);
  
  customText.value = sanitized;
};

/**
 * Fetch and update the preview image.
 */
export const updatePreviewImage = async (text: string = customText.value) => {
  const previewUrl = config.value.previewUrl;
  const variantId = selectedVariantId.value;
  
  if (!previewUrl || !variantId) {
    return;
  }
  
  isLoadingPreview.value = true;
  
  const url = `${previewUrl}?variantId=gid://shopify/ProductVariant/${variantId}&text=${text}`;
  
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error("Failed to fetch preview image");
      isLoadingPreview.value = false;
      return;
    }
    
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    
    // Revoke old blob URL to prevent memory leaks
    if (previewImageUrl.value && previewImageUrl.value.startsWith("blob:")) {
      URL.revokeObjectURL(previewImageUrl.value);
    }
    
    previewImageUrl.value = blobUrl;
    isLoadingPreview.value = false;
  } catch (error) {
    console.error("Error fetching preview image:", error);
    isLoadingPreview.value = false;
  }
};

/**
 * Open the customization modal.
 */
export const openCustomizationModal = () => {
  console.log("[customization-store] Opening modal");
  isCustomizationModalOpen.value = true;
  userWantsToCustomize.value = true;
  
  // Load initial preview if we have a variant
  if (selectedVariantId.value) {
    updatePreviewImage("");
  }
};

/**
 * Close the customization modal.
 */
export const closeCustomizationModal = () => {
  isCustomizationModalOpen.value = false;
};

/**
 * Open the confirmation modal.
 */
export const openConfirmationModal = () => {
  isConfirmationModalOpen.value = true;
};

/**
 * Close the confirmation modal.
 */
export const closeConfirmationModal = () => {
  isConfirmationModalOpen.value = false;
};

/**
 * Reset customization state (after add to cart or cancel).
 */
export const resetCustomization = () => {
  customText.value = "";
  userWantsToCustomize.value = false;
  isCustomizationModalOpen.value = false;
  isConfirmationModalOpen.value = false;
  
  // Revoke blob URL
  if (previewImageUrl.value && previewImageUrl.value.startsWith("blob:")) {
    URL.revokeObjectURL(previewImageUrl.value);
  }
  previewImageUrl.value = null;
  
  // Reset preview to empty
  if (selectedVariantId.value) {
    updatePreviewImage("");
  }
};

/**
 * Add custom text to main product form as a hidden input.
 */
export const addCustomTextToForm = () => {
  const mainForm = document.querySelector('form[id^="product-form-template"]') as HTMLFormElement | null;
  if (!mainForm) return;
  
  // Remove existing custom text input
  const existing = mainForm.querySelector('input[name="properties[Your Text]"]');
  if (existing) {
    existing.remove();
  }
  
  // Add new hidden input with current value
  if (customText.value.trim()) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "properties[Your Text]";
    input.value = customText.value;
    mainForm.appendChild(input);
  }
};
